import {
  setFundingAllocation,
  setFundingMode,
  toggleFundingSource,
} from '@/model/ltcFinancialPlanningModel';
import { calculateTotalContribution } from '@/util/ltcFinancialPlanning';
import { useLtcPlanningCalculations } from '@client/hooks/useLtcPlanningCalculations';
import { AppState } from '@client/model/store';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

function getPaymentOption(policy: PolicyQuote | null): LtcFinancialPlanningPaymentOption {
  if (!policy) return 'Single Pay';

  const source =
    policy.cobPaymentOption?.toLowerCase() || policy.productFundingOption?.toLowerCase() || '';

  if (source.includes('10') || source.includes('ten')) return '10-Pay';
  if (source.includes('monthly')) return 'Monthly';
  if (source.includes('annual')) return 'Annually';

  return 'Single Pay';
}

function getPaymentDescription(
  paymentOption: LtcFinancialPlanningPaymentOption,
  contributionMonthCount: number,
): string {
  switch (paymentOption) {
    case '10-Pay':
      return 'over 10 years';
    case 'Monthly':
    case 'Annually':
      return `over ${Math.round(contributionMonthCount / 12)} years`;
    default:
      return 'one-time payment';
  }
}

export function useFundingStep() {
  const dispatch = useDispatch();
  const { yearsTillLtc, contributionMonthCount } = useLtcPlanningCalculations();

  const {
    selectedAssets,
    priorityAssets,
    accountDetails,
    chosenPolicy,
    fundingAllocations,
    fundingModes,
  } = useSelector(
    (state: { ltcFinancialPlanning: AppState['ltcFinancialPlanning'] }) =>
      state.ltcFinancialPlanning,
  );

  // Determine payment option from chosen policy
  const paymentOption = getPaymentOption(chosenPolicy);

  // Calculate total policy cost based on payment type
  const policyPremium = useMemo(() => {
    switch (paymentOption) {
      case '10-Pay':
        return (chosenPolicy?.premiumAmount ?? 0) * 10;
      case 'Monthly':
      case 'Annually':
        return ((chosenPolicy?.premiumAmount ?? 0) * contributionMonthCount) / 12;
      default:
        return chosenPolicy?.premiumAmount ?? 0;
    }
  }, [chosenPolicy, paymentOption, contributionMonthCount]);

  // Assets available for funding (not protected)
  const fundableAssets = useMemo(
    () => selectedAssets.filter(asset => !priorityAssets.includes(asset)),
    [selectedAssets, priorityAssets],
  );

  // Calculate total allocation considering both modes
  const totalAllocation = useMemo(
    () =>
      Object.entries(fundingAllocations).reduce((sum, [assetType, allocation]) => {
        const mode = fundingModes[assetType as LtcFinancialPlanningAssetType] || 'lumpSum';
        return (
          sum +
          calculateTotalContribution(allocation ?? 0, mode, paymentOption, contributionMonthCount)
        );
      }, 0),
    [fundingAllocations, fundingModes, paymentOption, contributionMonthCount],
  );

  const remainingToFund = Math.max(0, policyPremium - totalAllocation);
  const isFunded = totalAllocation >= policyPremium;
  const fundingProgress = Math.min(100, (totalAllocation / policyPremium) * 100);

  // Payment description for display
  const paymentDescription = getPaymentDescription(paymentOption, contributionMonthCount);

  // Handlers
  const handleToggleFundingSource = useCallback(
    (assetType: LtcFinancialPlanningAssetType) => {
      dispatch(toggleFundingSource(assetType));
    },
    [dispatch],
  );

  const handleFundingModeChange = useCallback(
    (assetType: LtcFinancialPlanningAssetType, mode: LtcFinancialPlanningFundingMode) => {
      dispatch(setFundingMode({ assetType, mode }));
    },
    [dispatch],
  );

  const handleAllocationChange = useCallback(
    (assetType: LtcFinancialPlanningAssetType, amount: number) => {
      const mode = fundingModes[assetType] || 'lumpSum';
      const accountDetail = accountDetails[assetType];

      // Get the max for this mode
      const maxForMode =
        mode === 'lumpSum'
          ? (accountDetail?.currentValue ?? 0)
          : (accountDetail?.futureContributions ?? 0);

      // Calculate total contribution for this asset with the new amount
      const thisAssetTotal = calculateTotalContribution(
        amount,
        mode,
        paymentOption,
        contributionMonthCount,
      );

      // Calculate total from other assets
      const otherAssetsTotal = Object.entries(fundingAllocations).reduce(
        (sum, [asset, allocation]) => {
          if (asset === assetType) return sum;
          const assetMode = fundingModes[asset as LtcFinancialPlanningAssetType] || 'lumpSum';
          return (
            sum +
            calculateTotalContribution(
              allocation ?? 0,
              assetMode,
              paymentOption,
              contributionMonthCount,
            )
          );
        },
        0,
      );

      // Calculate max allowed to not exceed policy cost
      const maxAllowedTotal = Math.max(0, policyPremium - otherAssetsTotal);

      // Clamp the amount
      let clampedAmount = Math.max(0, Math.min(amount, maxForMode));

      // Also ensure the total contribution doesn't exceed policy cost
      const clampedTotal = calculateTotalContribution(
        clampedAmount,
        mode,
        paymentOption,
        contributionMonthCount,
      );

      if (clampedTotal > maxAllowedTotal) {
        // Need to reduce the amount to fit within policy cost
        if (mode === 'lumpSum') {
          clampedAmount = Math.min(clampedAmount, maxAllowedTotal);
        } else {
          // For monthly, we need to reverse the calculation
          const multiplier = paymentOption === '10-Pay' ? 120 : contributionMonthCount;
          clampedAmount = Math.min(clampedAmount, Math.floor(maxAllowedTotal / multiplier));
        }
      }

      dispatch(setFundingAllocation({ assetType, amount: clampedAmount }));
    },
    [
      dispatch,
      fundingModes,
      accountDetails,
      paymentOption,
      contributionMonthCount,
      fundingAllocations,
      policyPremium,
    ],
  );

  // Helper to get asset card data
  const getAssetCardData = useCallback(
    (assetType: LtcFinancialPlanningAssetType) => {
      const detail = accountDetails[assetType] || {
        currentValue: 50000,
        futureContributions: 0,
        expectedReturn: 7,
        capitalGainsTaxRate: 15,
        isComplete: false,
      };
      const allocation = fundingAllocations[assetType] || 0;
      const mode = fundingModes[assetType] || 'lumpSum';
      const isSelected = allocation > 0 || fundingAllocations[assetType] !== undefined;

      return { detail, allocation, mode, isSelected };
    },
    [accountDetails, fundingAllocations, fundingModes],
  );

  return {
    // Data
    yearsTillLtc,
    contributionMonthCount,
    paymentOption,
    policyPremium,
    fundableAssets,
    priorityAssets,
    totalAllocation,
    remainingToFund,
    isFunded,
    fundingProgress,
    paymentDescription,

    // Handlers
    handleToggleFundingSource,
    handleFundingModeChange,
    handleAllocationChange,

    // Helpers
    getAssetCardData,
  };
}

